import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import ContentMatrixCard from "@/components/content-matrix-card";
import PlaybookCard from "@/components/playbook-card";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Compass, Send, Save, ArrowRight, Search, RotateCcw,
  Linkedin, Globe, Target, Calendar, Clock, Hash, MessageSquare,
  Megaphone, Users, DollarSign, TrendingUp, RefreshCw, Pencil, Check, X, Plus, Trash2,
  BarChart3, BarChart2, BrainCircuit, Shield, Zap, ExternalLink, AlertTriangle, ChevronDown, ChevronRight,
  Rocket, Radar, Star, BookmarkCheck, Bookmark, XCircle, CheckCircle2, Activity,
  Lock, Sparkles, History, GitCompare,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { SiX, SiMeta } from "react-icons/si";
import type { MarketingPlan, StructuredStrategy, PlatformPlan, AdPlan, LeadPlan, MarketingOffer } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface AnalysisResult {
  summary: string;
  competitors: string[];
  swot: { strengths: string[]; weaknesses: string[]; opportunities: string[]; threats: string[] };
  strategy: string;
  sources?: { uri?: string; title?: string }[];
}

interface ChatAction { type: string; label: string; href?: string; description?: string; }
interface ChatMessage { role: "user" | "assistant"; content: string; actions?: ChatAction[]; }

type PageState = "idle" | "analyzing" | "results" | "chatting";
type StrategyTab = "overview" | "channels" | "campaign-hub" | "radar" | "analytics" | "audit" | "revisions";

const TAB_CONFIG: { key: StrategyTab; label: string; icon: any }[] = [
  { key: "overview", label: "Overview", icon: Compass },
  { key: "channels", label: "Channels", icon: Globe },
  { key: "campaign-hub", label: "Campaign Hub", icon: Megaphone },
  { key: "radar", label: "Radar", icon: Radar },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "audit", label: "Audit", icon: BarChart2 },
  { key: "revisions", label: "Strategy History", icon: History },
];


function EditableField({ value, onSave, multiline = false, className = "", placeholder }: { value: string; onSave: (v: string) => void; multiline?: boolean; className?: string; placeholder?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  if (!editing) {
    return (
      <div
        className={`group cursor-pointer flex items-start gap-1.5 ${className}`}
        onClick={() => { setDraft(value); setEditing(true); }}
        data-testid="editable-field"
      >
        <span className={`flex-1 ${!value ? "text-muted-foreground/50 italic" : ""}`}>{value || placeholder || "Click to edit..."}</span>
        <Pencil size={10} className="text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1.5">
      {multiline ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 bg-muted/50 border border-blue-500/40 rounded-lg px-2 py-1 text-sm text-foreground outline-none resize-none min-h-[60px]"
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
          data-testid="editable-textarea"
        />
      ) : (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 bg-muted/50 border border-blue-500/40 rounded-lg px-2 py-1 text-sm text-foreground outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter") { onSave(draft); setEditing(false); }
            if (e.key === "Escape") setEditing(false);
          }}
          data-testid="editable-input"
        />
      )}
      <button onClick={() => { onSave(draft); setEditing(false); }} className="text-green-400 hover:text-green-300 p-0.5" data-testid="button-confirm-edit">
        <Check size={14} />
      </button>
      <button onClick={() => setEditing(false)} className="text-muted-foreground/70 hover:text-muted-foreground p-0.5" data-testid="button-cancel-edit">
        <X size={14} />
      </button>
    </div>
  );
}

function PlatformPlanView({ plan, onUpdate }: { plan: PlatformPlan; onUpdate: (updated: PlatformPlan) => void }) {
  const [newHashtag, setNewHashtag] = useState("");
  const [newContentType, setNewContentType] = useState("");
  const [addingCalendarRow, setAddingCalendarRow] = useState(false);
  const [newRow, setNewRow] = useState({ day: "Monday", contentType: "Article", topic: "", time: "9:00 AM" });

  if (!plan?.platform) return null;

  return (
    <div className="space-y-6" data-testid={`platform-plan-${(plan.platform ?? "").toLowerCase()}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
            <Target size={12} /> Objective
          </div>
          <EditableField
            value={plan.objective}
            onSave={(v) => onUpdate({ ...plan, objective: v })}
            className="text-sm text-foreground/80"
            multiline
          />
        </div>
        <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
            <Clock size={12} /> Frequency
          </div>
          <EditableField
            value={plan.postingFrequency}
            onSave={(v) => onUpdate({ ...plan, postingFrequency: v })}
            className="text-sm text-foreground/80"
          />
          <div className="flex flex-wrap gap-1 mt-1">
            {plan.bestTimes?.map((t, i) => (
              <div key={i} className="group flex items-center gap-0.5">
                <span className="text-[10px] bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded-md cursor-pointer" onClick={() => {
                  const val = prompt("Edit time:", t);
                  if (val !== null) {
                    const updated = [...(plan.bestTimes || [])];
                    updated[i] = val;
                    onUpdate({ ...plan, bestTimes: updated });
                  }
                }}>{t}</span>
                <button className="text-red-400/0 group-hover:text-red-400/60 hover:text-red-400 transition-colors" onClick={() => {
                  onUpdate({ ...plan, bestTimes: (plan.bestTimes || []).filter((_, j) => j !== i) });
                }}><Trash2 size={10} /></button>
              </div>
            ))}
            <button
              className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-md hover:bg-blue-500/20 transition-colors"
              onClick={() => {
                const val = prompt("Add a posting time (e.g. Tuesday 9am):");
                if (val) onUpdate({ ...plan, bestTimes: [...(plan.bestTimes || []), val] });
              }}
              data-testid="button-add-time"
            >
              <Plus size={10} className="inline" /> Add
            </button>
          </div>
        </div>
        <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
            <MessageSquare size={12} /> Tone
          </div>
          <EditableField
            value={plan.toneAndStyle}
            onSave={(v) => onUpdate({ ...plan, toneAndStyle: v })}
            className="text-sm text-foreground/80"
            multiline
          />
        </div>
      </div>

      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2" data-testid="custom-instructions-card">
        <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
          <Shield size={12} /> Custom Instructions
        </div>
        <p className="text-[10px] text-muted-foreground/60">Override tone, add guardrails, or specify exactly what to write. Injected into every AI generation call for this channel.</p>
        <EditableField
          value={plan.customInstructions || ""}
          onSave={(v) => onUpdate({ ...plan, customInstructions: v })}
          className="text-sm text-foreground/80"
          multiline
          placeholder="e.g. Always use a professional tone. Never mention competitor names. Every post must include a call-to-action."
        />
      </div>

      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
          <Hash size={12} /> Content Types & Hashtags
        </div>
        <div className="flex flex-wrap gap-2">
          {plan.contentTypes?.map((ct, i) => (
            <div key={i} className="group flex items-center gap-0.5">
              <span className="text-xs bg-muted/50 text-muted-foreground px-3 py-1 rounded-lg border border-border">{ct}</span>
              <button className="text-red-400/0 group-hover:text-red-400/60 hover:text-red-400 transition-colors" onClick={() => {
                onUpdate({ ...plan, contentTypes: (plan.contentTypes || []).filter((_, j) => j !== i) });
              }}><Trash2 size={10} /></button>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <input
              value={newContentType}
              onChange={(e) => setNewContentType(e.target.value)}
              placeholder="Add type..."
              className="text-xs bg-muted/50 border border-border rounded-lg px-2 py-1 text-foreground outline-none w-24 focus:border-blue-500/40"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newContentType.trim()) {
                  onUpdate({ ...plan, contentTypes: [...(plan.contentTypes || []), newContentType.trim()] });
                  setNewContentType("");
                }
              }}
              data-testid="input-add-content-type"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {plan.hashtags?.map((h, i) => (
            <div key={i} className="group flex items-center gap-0.5">
              <span className="text-[10px] text-blue-400">{h}</span>
              <button className="text-red-400/0 group-hover:text-red-400/60 hover:text-red-400 transition-colors" onClick={() => {
                onUpdate({ ...plan, hashtags: (plan.hashtags || []).filter((_, j) => j !== i) });
              }}><Trash2 size={10} /></button>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <input
              value={newHashtag}
              onChange={(e) => setNewHashtag(e.target.value)}
              placeholder="#add..."
              className="text-[10px] bg-transparent border-b border-border px-1 py-0.5 text-blue-400 outline-none w-20 focus:border-blue-500/40"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newHashtag.trim()) {
                  const tag = newHashtag.trim().startsWith("#") ? newHashtag.trim() : `#${newHashtag.trim()}`;
                  onUpdate({ ...plan, hashtags: [...(plan.hashtags || []), tag] });
                  setNewHashtag("");
                }
              }}
              data-testid="input-add-hashtag"
            />
          </div>
        </div>
      </div>

      <div className="bg-card/50 border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider">
            <Calendar size={12} /> Weekly Calendar
          </div>
          <button
            onClick={() => setAddingCalendarRow(true)}
            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
            data-testid="button-add-calendar-entry"
          >
            <Plus size={12} /> Add Entry
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-muted-foreground text-xs font-bold uppercase">Day</th>
                <th className="text-left py-2 px-3 text-muted-foreground text-xs font-bold uppercase">Type</th>
                <th className="text-left py-2 px-3 text-muted-foreground text-xs font-bold uppercase">Topic</th>
                <th className="text-left py-2 px-3 text-muted-foreground text-xs font-bold uppercase">Time</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(plan.weeklyCalendar || []).map((entry, i) => (
                <tr key={i} className="border-b border-border hover:bg-muted/30 group">
                  <td className="py-2.5 px-3">
                    <select
                      value={entry.day}
                      onChange={(e) => {
                        const cal = [...(plan.weeklyCalendar || [])];
                        cal[i] = { ...cal[i], day: e.target.value };
                        onUpdate({ ...plan, weeklyCalendar: cal });
                      }}
                      className="bg-transparent text-foreground/70 font-medium text-sm outline-none cursor-pointer"
                      data-testid={`select-day-${i}`}
                    >
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => <option key={d} value={d} className="bg-gray-900">{d}</option>)}
                    </select>
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      value={entry.contentType}
                      onChange={(e) => {
                        const cal = [...(plan.weeklyCalendar || [])];
                        cal[i] = { ...cal[i], contentType: e.target.value };
                        onUpdate({ ...plan, weeklyCalendar: cal });
                      }}
                      className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded-md outline-none w-24 focus:ring-1 focus:ring-blue-500/30"
                      data-testid={`input-type-${i}`}
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      value={entry.topic}
                      onChange={(e) => {
                        const cal = [...(plan.weeklyCalendar || [])];
                        cal[i] = { ...cal[i], topic: e.target.value };
                        onUpdate({ ...plan, weeklyCalendar: cal });
                      }}
                      className="text-muted-foreground bg-transparent outline-none w-full focus:text-foreground text-sm"
                      data-testid={`input-topic-${i}`}
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <input
                      value={entry.time}
                      onChange={(e) => {
                        const cal = [...(plan.weeklyCalendar || [])];
                        cal[i] = { ...cal[i], time: e.target.value };
                        onUpdate({ ...plan, weeklyCalendar: cal });
                      }}
                      className="text-muted-foreground text-xs bg-transparent outline-none w-20 focus:text-foreground"
                      data-testid={`input-time-${i}`}
                    />
                  </td>
                  <td className="py-2.5 px-1">
                    <button
                      className="text-red-400/0 group-hover:text-red-400/60 hover:text-red-400 transition-colors"
                      onClick={() => onUpdate({ ...plan, weeklyCalendar: (plan.weeklyCalendar || []).filter((_, j) => j !== i) })}
                      data-testid={`button-delete-row-${i}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {addingCalendarRow && (
                <tr className="border-b border-blue-500/20 bg-blue-500/5">
                  <td className="py-2.5 px-3">
                    <select value={newRow.day} onChange={(e) => setNewRow({ ...newRow, day: e.target.value })} className="bg-transparent text-foreground/70 text-sm outline-none" data-testid="select-new-day">
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => <option key={d} value={d} className="bg-gray-900">{d}</option>)}
                    </select>
                  </td>
                  <td className="py-2.5 px-3">
                    <input value={newRow.contentType} onChange={(e) => setNewRow({ ...newRow, contentType: e.target.value })} className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded-md outline-none w-24" placeholder="Type" data-testid="input-new-type" />
                  </td>
                  <td className="py-2.5 px-3">
                    <input value={newRow.topic} onChange={(e) => setNewRow({ ...newRow, topic: e.target.value })} className="text-muted-foreground bg-transparent outline-none w-full text-sm" placeholder="Topic" autoFocus data-testid="input-new-topic" />
                  </td>
                  <td className="py-2.5 px-3">
                    <input value={newRow.time} onChange={(e) => setNewRow({ ...newRow, time: e.target.value })} className="text-muted-foreground text-xs bg-transparent outline-none w-20" placeholder="Time" data-testid="input-new-time" />
                  </td>
                  <td className="py-2.5 px-1 flex items-center gap-1">
                    <button className="text-green-400 hover:text-green-300" onClick={() => {
                      if (newRow.topic.trim()) {
                        onUpdate({ ...plan, weeklyCalendar: [...(plan.weeklyCalendar || []), newRow] });
                        setNewRow({ day: "Monday", contentType: "Article", topic: "", time: "9:00 AM" });
                        setAddingCalendarRow(false);
                      }
                    }} data-testid="button-confirm-new-row"><Check size={14} /></button>
                    <button className="text-muted-foreground/70 hover:text-muted-foreground" onClick={() => setAddingCalendarRow(false)} data-testid="button-cancel-new-row"><X size={14} /></button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdPlansView({ plans, onUpdate }: { plans: AdPlan[]; onUpdate?: (index: number, updated: AdPlan) => void }) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3" data-testid="ad-plans-view">
      {plans.map((ad, i) => {
        const isExpanded = expandedIndex === i;
        return (
          <div key={i} className="bg-card/50 border border-border rounded-xl overflow-visible" data-testid={`ad-plan-card-${i}`}>
            <button
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
              onClick={() => setExpandedIndex(isExpanded ? null : i)}
              data-testid={`button-toggle-ad-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
                <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/20 shrink-0">{ad.platform}</Badge>
                <span className="text-sm font-bold text-foreground truncate">{ad.campaignType}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex items-center gap-1">
                  <DollarSign size={12} className="text-green-400" />
                  <span className="text-xs font-bold text-green-400">{ad.budget}</span>
                </div>
                {isExpanded ? <ChevronDown size={14} className="text-muted-foreground/70" /> : <ChevronRight size={14} className="text-muted-foreground/70" />}
              </div>
            </button>
            {isExpanded && (
              <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Objective</span>
                  {onUpdate ? (
                    <EditableField value={ad.objective} onSave={(v) => onUpdate(i, { ...ad, objective: v })} className="text-sm text-muted-foreground" multiline />
                  ) : (
                    <p className="text-sm text-muted-foreground">{ad.objective}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Target Audience</span>
                    {onUpdate ? (
                      <EditableField value={ad.targetAudience} onSave={(v) => onUpdate(i, { ...ad, targetAudience: v })} className="text-xs text-muted-foreground" multiline />
                    ) : (
                      <p className="text-xs text-muted-foreground">{ad.targetAudience}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Duration</span>
                    {onUpdate ? (
                      <EditableField value={ad.duration} onSave={(v) => onUpdate(i, { ...ad, duration: v })} className="text-xs text-muted-foreground" />
                    ) : (
                      <p className="text-xs text-muted-foreground">{ad.duration}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ad.adFormats?.map((f, j) => (
                    <span key={j} className="text-xs bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded-md">{f}</span>
                  ))}
                </div>
                {ad.keyMessages && ad.keyMessages.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Key Messages</span>
                    <ul className="space-y-1">
                      {ad.keyMessages.map((m, j) => (
                        <li key={j} className="text-xs text-muted-foreground flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                          {onUpdate ? (
                            <EditableField value={m} onSave={(v) => {
                              const msgs = [...(ad.keyMessages || [])];
                              msgs[j] = v;
                              onUpdate(i, { ...ad, keyMessages: msgs });
                            }} className="flex-1 text-xs text-muted-foreground" />
                          ) : m}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {plans.length === 0 && <p className="text-sm text-muted-foreground/70">No ad plans generated.</p>}
    </div>
  );
}

function LeadPlanView({ plan, onUpdate }: { plan: LeadPlan; onUpdate?: (updated: LeadPlan) => void }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="space-y-3" data-testid="lead-plan-view">
      <div className="bg-card/50 border border-border rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <Users size={16} className="text-blue-400 shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/20">{plan.channel}</Badge>
              {onUpdate ? (
                <select
                  value={plan.nurtureCadence || "weekly"}
                  onChange={(e) => onUpdate({ ...plan, nurtureCadence: e.target.value })}
                  className="text-xs bg-muted/50 border border-border rounded-md px-2 py-0.5 text-foreground/70 cursor-pointer"
                  data-testid="select-nurture-cadence"
                >
                  <option value="daily">Daily</option>
                  <option value="every 2 days">Every 2 days</option>
                  <option value="every 3 days">Every 3 days</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              ) : (
                <span className="text-xs text-muted-foreground">{plan.nurtureCadence}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-muted-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            data-testid="button-toggle-lead-details"
          >
            {showDetails ? "Less" : "More"}
            {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </div>

        <div className="space-y-1 mb-3">
          <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Approach</span>
          {onUpdate ? (
            <EditableField value={plan.approach} onSave={(v) => onUpdate({ ...plan, approach: v })} className="text-sm text-foreground/70" multiline />
          ) : (
            <p className="text-sm text-foreground/70">{plan.approach}</p>
          )}
        </div>

        {plan.keyTalkingPoints && plan.keyTalkingPoints.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {plan.keyTalkingPoints.slice(0, showDetails ? undefined : 3).map((p: any, i) => {
              const label = typeof p === 'string' ? p : (p?.topic || p?.week || JSON.stringify(p));
              return (
                <div key={i} className="text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded-md flex items-center gap-1" data-testid={`talking-point-${i}`}>
                  <TrendingUp size={9} className="shrink-0" />
                  <span className="truncate max-w-[200px]">{label}</span>
                </div>
              );
            })}
            {!showDetails && plan.keyTalkingPoints.length > 3 && (
              <span className="text-[10px] text-muted-foreground/50">+{plan.keyTalkingPoints.length - 3} more</span>
            )}
          </div>
        )}

        {showDetails && (
          <div className="mt-3 space-y-3 border-t border-border pt-3">
            {plan.keyTalkingPoints && plan.keyTalkingPoints.length > 0 && onUpdate && (
              <div className="space-y-1.5">
                <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Talking Points (editable)</span>
                {plan.keyTalkingPoints.map((p: any, i) => {
                  const strVal = typeof p === 'string' ? p : (p?.topic || p?.week || JSON.stringify(p));
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <TrendingUp size={12} className="text-green-400 mt-0.5 shrink-0" />
                      <EditableField value={strVal} onSave={(v) => {
                        const pts = [...(plan.keyTalkingPoints || [])];
                        pts[i] = v;
                        onUpdate({ ...plan, keyTalkingPoints: pts });
                      }} className="flex-1 text-xs text-muted-foreground" />
                    </div>
                  );
                })}
              </div>
            )}
            {plan.objectionHandling && plan.objectionHandling.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-[10px] text-muted-foreground/70 uppercase font-bold">Objection Handling</span>
                {plan.objectionHandling.map((o: any, i) => {
                  const strVal = typeof o === 'string' ? o : (o?.topic || o?.week || JSON.stringify(o));
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <MessageSquare size={12} className="text-blue-400 mt-0.5 shrink-0" />
                      {onUpdate ? (
                        <EditableField value={strVal} onSave={(v) => {
                          const objs = [...(plan.objectionHandling || [])];
                          objs[i] = v;
                          onUpdate({ ...plan, objectionHandling: objs });
                        }} className="flex-1 text-xs text-muted-foreground" />
                      ) : (
                        <span className="text-xs text-muted-foreground">{strVal}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OffersView({ painPoints, marketingOffers, workspaceId, onUpdate }: {
  painPoints: string[];
  marketingOffers: { aggressive: MarketingOffer[]; medium: MarketingOffer[]; general: MarketingOffer[] };
  workspaceId: number;
  onUpdate?: (updated: { aggressive: MarketingOffer[]; medium: MarketingOffer[]; general: MarketingOffer[] }) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [generatingTier, setGeneratingTier] = useState<string | null>(null);

  const updateOffer = (tier: "aggressive" | "medium" | "general", index: number, field: keyof MarketingOffer, value: string) => {
    if (!onUpdate) return;
    const updated = { ...marketingOffers };
    updated[tier] = updated[tier].map((o, i) => i === index ? { ...o, [field]: value } : o);
    onUpdate(updated);
  };

  const addOffer = (tier: "aggressive" | "medium" | "general") => {
    if (!onUpdate) return;
    const newOffer: MarketingOffer = { title: "New Offer", description: "Describe your offer here", cta: "Get Started", promo: "" };
    onUpdate({ ...marketingOffers, [tier]: [...(marketingOffers[tier] || []), newOffer] });
  };

  const removeOffer = (tier: "aggressive" | "medium" | "general", index: number) => {
    if (!onUpdate) return;
    onUpdate({ ...marketingOffers, [tier]: marketingOffers[tier].filter((_, i) => i !== index) });
  };

  const generateAds = async (tier: "aggressive" | "medium" | "general") => {
    setGeneratingTier(tier);
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/tiered-ads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tier, platforms: ["Meta", "LinkedIn", "X"] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to generate ads");
      }
      const data = await res.json();
      toast({ title: "Ads Created", description: `Peggy generated ${data.count} ${tier} tier ads. Check Content Studio to approve them.` });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "content"] });
    } catch (e: any) {
      toast({ title: "Generation Failed", description: e.message, variant: "destructive" });
    }
    setGeneratingTier(null);
  };

  const tierConfig = [
    { key: "aggressive" as const, label: "Aggressive", sublabel: "Hard sell, FOMO, urgency, scarcity", color: "from-red-500 to-red-600", textColor: "text-red-400", borderColor: "border-red-500/20", bgColor: "bg-red-500/5", icon: Zap },
    { key: "medium" as const, label: "Medium", sublabel: "Value-focused, social proof, moderate urgency", color: "from-amber-500 to-amber-600", textColor: "text-amber-400", borderColor: "border-amber-500/20", bgColor: "bg-amber-500/5", icon: TrendingUp },
    { key: "general" as const, label: "General", sublabel: "Brand awareness, education, thought leadership", color: "from-blue-500 to-blue-600", textColor: "text-blue-400", borderColor: "border-blue-500/20", bgColor: "bg-blue-500/5", icon: Compass },
  ];

  return (
    <div className="space-y-6" data-testid="offers-view">
      {painPoints.length > 0 && (
        <div className="bg-card/50 border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
              <AlertTriangle size={15} className="text-red-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Customer Pain Points</h3>
              <p className="text-[11px] text-muted-foreground/70">Discovered by Draper during market research</p>
            </div>
          </div>
          <div className="space-y-2">
            {painPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-muted/30 border border-border rounded-xl px-4 py-3" data-testid={`pain-point-${i}`}>
                <div className="w-6 h-6 rounded-md bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] font-black text-red-400">{i + 1}</span>
                </div>
                <p className="text-sm text-foreground/70 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {painPoints.length === 0 && (
        <div className="text-center py-12 space-y-3">
          <AlertTriangle size={32} className="text-muted-foreground/40 mx-auto" />
          <p className="text-muted-foreground/70 text-sm">No pain points discovered yet. Run a strategy analysis first.</p>
          <p className="text-muted-foreground/50 text-xs">Draper will discover customer pain points and generate tiered marketing offers during the analysis.</p>
        </div>
      )}

      {painPoints.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Megaphone size={15} className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Tiered Marketing Offers</h3>
              <p className="text-[11px] text-muted-foreground/70">Have Peggy create ads for each tier, then approve in Content Studio</p>
            </div>
          </div>

          {tierConfig.map(tc => {
            const offers = marketingOffers[tc.key] || [];
            return (
              <div key={tc.key} className={`${tc.bgColor} border ${tc.borderColor} rounded-2xl p-5 space-y-4`} data-testid={`offer-tier-${tc.key}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${tc.color} flex items-center justify-center`}>
                      <tc.icon size={16} className="text-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className={`text-sm font-bold ${tc.textColor}`}>{tc.label}</h4>
                        <Badge variant="outline" className={`text-[10px] ${tc.textColor} ${tc.borderColor}`}>
                          {offers.length} offers
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground/70">{tc.sublabel}</p>
                    </div>
                  </div>
                  <Button
                    data-testid={`button-generate-${tc.key}-ads`}
                    onClick={() => generateAds(tc.key)}
                    disabled={generatingTier !== null || offers.length === 0}
                    className="bg-muted hover:bg-muted text-foreground border border-border"
                  >
                    {generatingTier === tc.key ? (
                      <Loader2 size={14} className="animate-spin mr-2" />
                    ) : (
                      <Zap size={14} className="mr-2" />
                    )}
                    {generatingTier === tc.key ? "Peggy creating..." : "Generate Ads"}
                  </Button>
                </div>

                {offers.length > 0 ? (
                  <div className="grid gap-3">
                    {offers.map((offer, i) => (
                      <div key={i} className="bg-background/20 border border-border rounded-xl p-4 space-y-2" data-testid={`offer-${tc.key}-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <EditableField
                            value={offer.title}
                            onSave={(v) => updateOffer(tc.key, i, "title", v)}
                            className="text-sm font-bold text-foreground/80 flex-1"
                          />
                          {onUpdate && (
                            <button onClick={() => removeOffer(tc.key, i)} className="text-muted-foreground/40 hover:text-red-400 transition-colors shrink-0 mt-0.5" data-testid={`button-remove-offer-${tc.key}-${i}`}>
                              <X size={13} />
                            </button>
                          )}
                        </div>
                        <EditableField
                          value={offer.description}
                          onSave={(v) => updateOffer(tc.key, i, "description", v)}
                          className="text-xs text-muted-foreground leading-relaxed"
                          multiline
                        />
                        {offer.promo !== undefined && (
                          <div className="space-y-0.5">
                            <span className="text-[10px] text-muted-foreground/60 uppercase font-bold">Promo / Special Offer</span>
                            <EditableField
                              value={offer.promo || ""}
                              onSave={(v) => updateOffer(tc.key, i, "promo", v)}
                              className="text-xs text-amber-400"
                              placeholder="e.g. 20% off this month, free consultation..."
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] text-muted-foreground/60 uppercase font-bold">CTA:</span>
                          <EditableField
                            value={offer.cta}
                            onSave={(v) => updateOffer(tc.key, i, "cta", v)}
                            className={`text-xs font-semibold ${tc.textColor}`}
                          />
                        </div>
                      </div>
                    ))}
                    {onUpdate && (
                      <button
                        onClick={() => addOffer(tc.key)}
                        className={`w-full border border-dashed ${tc.borderColor} rounded-xl py-2.5 text-xs ${tc.textColor} hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5`}
                        data-testid={`button-add-offer-${tc.key}`}
                      >
                        <Plus size={12} /> Add offer
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground/60">No offers in this tier. Re-run strategy analysis or add manually.</p>
                    {onUpdate && (
                      <button
                        onClick={() => addOffer(tc.key)}
                        className={`border border-dashed ${tc.borderColor} rounded-xl px-4 py-2 text-xs ${tc.textColor} hover:bg-white/5 transition-colors flex items-center gap-1.5`}
                        data-testid={`button-add-offer-${tc.key}-empty`}
                      >
                        <Plus size={12} /> Add offer
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Channels View (all 4 platforms collapsible in one page) ─────────────────
function ChannelsView({
  structuredStrategy,
  channelPrefs,
  handleToggleChannel,
  handlePlatformUpdate,
  getPlatformPlan,
}: {
  structuredStrategy: StructuredStrategy;
  channelPrefs: { linkedin: boolean; x: boolean; meta: boolean; google: boolean };
  handleToggleChannel: (k: "linkedin" | "x" | "meta" | "google") => void;
  handlePlatformUpdate: (updated: PlatformPlan) => void;
  getPlatformPlan: (platform: string) => PlatformPlan | undefined;
}) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    LinkedIn: true, X: false, Meta: false, Google: false,
  });
  const toggle = (k: string) => setOpenSections(s => ({ ...s, [k]: !s[k] }));

  const channels: { key: "linkedin" | "x" | "meta" | "google"; label: string; platform: string; Icon: any }[] = [
    { key: "linkedin", label: "LinkedIn", platform: "LinkedIn", Icon: Linkedin },
    { key: "x", label: "X / Twitter", platform: "X", Icon: SiX },
    { key: "meta", label: "Meta (FB + IG)", platform: "Meta", Icon: SiMeta },
    { key: "google", label: "Google", platform: "Google", Icon: Globe },
  ];

  return (
    <div className="space-y-3" data-testid="channels-view">
      <div className="flex items-center gap-3 flex-wrap pb-1">
        <span className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Active Channels</span>
        {channels.map(ch => (
          <button
            key={ch.key}
            data-testid={`toggle-channel-${ch.key}`}
            onClick={() => handleToggleChannel(ch.key)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              channelPrefs[ch.key]
                ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                : "bg-card/50 text-muted-foreground/70 border border-border"
            }`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${channelPrefs[ch.key] ? "bg-blue-400" : "bg-foreground/20"}`} />
            {ch.label}
          </button>
        ))}
      </div>

      {channels.map(ch => {
        const plan = getPlatformPlan(ch.platform);
        const isOpen = openSections[ch.platform];
        const enabled = channelPrefs[ch.key];
        return (
          <div
            key={ch.key}
            className={`border rounded-2xl overflow-hidden transition-all ${enabled ? "border-border" : "border-border/40 opacity-60"}`}
            data-testid={`channel-section-${ch.key}`}
          >
            <button
              className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-card/50 hover:bg-card/80 transition-colors"
              onClick={() => toggle(ch.platform)}
              data-testid={`button-toggle-channel-${ch.key}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <ch.Icon size={14} className="text-blue-400" />
                </div>
                <div className="text-left">
                  <span className="font-semibold text-sm text-foreground">{ch.label}</span>
                  {plan && !isOpen && (
                    <p className="text-xs text-muted-foreground/60 line-clamp-1 mt-0.5">{plan.objective}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!enabled && <span className="text-[10px] text-muted-foreground/50 font-bold uppercase">OFF</span>}
                {plan && (
                  <span className="text-[10px] bg-muted/50 text-muted-foreground px-2 py-0.5 rounded hidden sm:inline">{plan.postingFrequency}</span>
                )}
                {isOpen ? <ChevronDown size={16} className="text-muted-foreground/50" /> : <ChevronRight size={16} className="text-muted-foreground/50" />}
              </div>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-4 border-t border-border bg-card/20">
                {!enabled && (
                  <p className="text-xs text-muted-foreground/50 mb-3" data-testid={`text-channel-disabled-${ch.key}`}>
                    This channel is currently disabled. Enable it above to include it in content generation.
                  </p>
                )}
                {plan
                  ? <PlatformPlanView plan={plan} onUpdate={handlePlatformUpdate} />
                  : <p className="text-sm text-muted-foreground/60">No {ch.label} plan generated yet. Run strategy analysis to generate one.</p>
                }
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Campaign Hub View (Ad Plans + Offers + Lead Plan stacked) ────────────────
function CampaignHubView({
  structuredStrategy,
  workspaceId,
  handleAdPlanUpdate,
  handleLeadPlanUpdate,
  handleOffersUpdate,
}: {
  structuredStrategy: StructuredStrategy;
  workspaceId: number;
  handleAdPlanUpdate: (index: number, updated: AdPlan) => void;
  handleLeadPlanUpdate: (updated: LeadPlan) => void;
  handleOffersUpdate?: (updated: { aggressive: MarketingOffer[]; medium: MarketingOffer[]; general: MarketingOffer[] }) => void;
}) {
  const [openSections, setOpenSections] = useState({ ads: true, offers: true, leads: true });
  const toggle = (k: keyof typeof openSections) => setOpenSections(s => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-4" data-testid="campaign-hub-view">
      {/* Ad Plans */}
      <div className="border border-border rounded-2xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-card/50 hover:bg-card/80 transition-colors"
          onClick={() => toggle("ads")}
          data-testid="button-toggle-ad-plans"
        >
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Megaphone size={14} className="text-amber-400" />
            </div>
            <span className="font-semibold text-sm text-foreground">Ad Plans</span>
            <span className="text-[10px] bg-muted/50 text-muted-foreground px-2 py-0.5 rounded">{(structuredStrategy.adPlans || []).length} plans</span>
          </div>
          {openSections.ads ? <ChevronDown size={16} className="text-muted-foreground/50" /> : <ChevronRight size={16} className="text-muted-foreground/50" />}
        </button>
        {openSections.ads && (
          <div className="px-5 pb-5 pt-4 border-t border-border bg-card/20">
            <AdPlansView plans={structuredStrategy.adPlans || []} onUpdate={handleAdPlanUpdate} />
          </div>
        )}
      </div>

      {/* Marketing Offers */}
      <div className="border border-border rounded-2xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-card/50 hover:bg-card/80 transition-colors"
          onClick={() => toggle("offers")}
          data-testid="button-toggle-offers"
        >
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Target size={14} className="text-purple-400" />
            </div>
            <span className="font-semibold text-sm text-foreground">Marketing Offers</span>
            <span className="text-[10px] bg-muted/50 text-muted-foreground px-2 py-0.5 rounded">
              {Object.values(structuredStrategy.marketingOffers || {}).flat().length} offers
            </span>
          </div>
          {openSections.offers ? <ChevronDown size={16} className="text-muted-foreground/50" /> : <ChevronRight size={16} className="text-muted-foreground/50" />}
        </button>
        {openSections.offers && (
          <div className="px-5 pb-5 pt-4 border-t border-border bg-card/20">
            <OffersView
              painPoints={structuredStrategy.painPoints || []}
              marketingOffers={structuredStrategy.marketingOffers || { aggressive: [], medium: [], general: [] }}
              workspaceId={workspaceId}
              onUpdate={handleOffersUpdate}
            />
          </div>
        )}
      </div>

      {/* Lead Plan */}
      <div className="border border-border rounded-2xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-card/50 hover:bg-card/80 transition-colors"
          onClick={() => toggle("leads")}
          data-testid="button-toggle-leads"
        >
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Users size={14} className="text-emerald-400" />
            </div>
            <span className="font-semibold text-sm text-foreground">Lead Plan</span>
          </div>
          {openSections.leads ? <ChevronDown size={16} className="text-muted-foreground/50" /> : <ChevronRight size={16} className="text-muted-foreground/50" />}
        </button>
        {openSections.leads && (
          <div className="px-5 pb-5 pt-4 border-t border-border bg-card/20">
            {structuredStrategy.leadPlan
              ? <LeadPlanView plan={structuredStrategy.leadPlan} onUpdate={handleLeadPlanUpdate} />
              : <p className="text-sm text-muted-foreground/60">No lead plan generated yet.</p>
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ── Radar Inline View ──────────────────────────────────────────────────────────
type ScoreTier = "high" | "medium" | "low";
function getScoreColor(score: number): string {
  if (score >= 75) return "text-green-400";
  if (score >= 50) return "text-amber-400";
  return "text-red-400";
}
function getScoreBg(score: number): string {
  if (score >= 75) return "bg-green-500/10 border-green-500/20";
  if (score >= 50) return "bg-amber-500/10 border-amber-500/20";
  return "bg-red-500/10 border-red-500/20";
}

function RadarInlineView({ workspaceId }: { workspaceId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: opportunities, isLoading: oppsLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", workspaceId, "opportunities"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/opportunities`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!workspaceId,
  });

  const runScan = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${workspaceId}/opportunities/scan`, {});
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "opportunities"] });
      toast({ title: "Radar Scan Complete", description: `Found ${data.found || 0} new opportunities.` });
    },
    onError: (e: any) => toast({ title: "Scan failed", description: e.message, variant: "destructive" }),
  });

  const dismissOpp = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${workspaceId}/opportunities/${id}`, { status: "dismissed" });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "opportunities"] }),
  });

  const saveOpp = useMutation({
    mutationFn: async ({ id, isSaved }: { id: number; isSaved: boolean }) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${workspaceId}/opportunities/${id}`, { status: isSaved ? "new" : "saved" });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "opportunities"] }),
  });

  const visible = (opportunities || []).filter((o: any) => o.status !== "dismissed");
  const saved = visible.filter((o: any) => o.status === "saved");
  const unsaved = visible.filter((o: any) => o.status !== "saved");

  return (
    <div className="space-y-4" data-testid="radar-inline-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Radar size={16} className="text-blue-400" /> Draper's Radar
          </h3>
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            Prospects, job postings, and grant opportunities discovered by Draper.
          </p>
        </div>
        <Button
          onClick={() => runScan.mutate()}
          disabled={runScan.isPending}
          data-testid="button-run-radar-scan"
          className="bg-blue-600 hover:bg-blue-500 text-white text-xs gap-2"
          size="sm"
        >
          {runScan.isPending ? <Loader2 size={13} className="animate-spin" /> : <Radar size={13} />}
          {runScan.isPending ? "Scanning..." : "Run Radar Scan"}
        </Button>
      </div>

      {oppsLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map(i => <Skeleton key={i} className="h-24 w-full bg-card/50 rounded-xl" />)}
        </div>
      )}

      {!oppsLoading && visible.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border rounded-2xl">
          <Radar size={32} className="text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground/60 text-sm font-medium">No opportunities found yet.</p>
          <p className="text-muted-foreground/40 text-xs mt-1">Click "Run Radar Scan" to have Draper search for prospects.</p>
        </div>
      )}

      {saved.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Saved</p>
          {saved.map((opp: any) => (
            <RadarOppCard key={opp.id} opp={opp} onDismiss={() => dismissOpp.mutate(opp.id)} onSave={() => saveOpp.mutate({ id: opp.id, isSaved: true })} />
          ))}
        </div>
      )}

      {unsaved.length > 0 && (
        <div className="space-y-2">
          {saved.length > 0 && <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">New</p>}
          {unsaved.map((opp: any) => (
            <RadarOppCard key={opp.id} opp={opp} onDismiss={() => dismissOpp.mutate(opp.id)} onSave={() => saveOpp.mutate({ id: opp.id, isSaved: false })} />
          ))}
        </div>
      )}
    </div>
  );
}

function RadarOppCard({ opp, onDismiss, onSave }: { opp: any; onDismiss: () => void; onSave: () => void }) {
  const score = opp.relevanceScore || opp.score || 0;
  const isSaved = opp.status === "saved";
  return (
    <div
      className={`border rounded-xl p-4 space-y-2 transition-all hover:shadow-sm ${getScoreBg(score)}`}
      data-testid={`radar-opp-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold font-mono ${getScoreColor(score)}`}>{score}</span>
            {opp.type && (
              <span className="text-[10px] bg-muted/50 text-muted-foreground px-2 py-0.5 rounded uppercase font-bold">{opp.type?.replace(/_/g, " ")}</span>
            )}
            {opp.source && (
              <span className="text-[10px] text-muted-foreground/50">{opp.source}</span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-foreground mt-1 line-clamp-2">{opp.title}</h4>
          {opp.description && (
            <p className="text-xs text-muted-foreground/70 line-clamp-3 mt-0.5">{opp.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onSave}
            className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground/50 hover:text-amber-400"
            data-testid={`button-save-opp-${opp.id}`}
            title={isSaved ? "Unsave" : "Save"}
          >
            {isSaved ? <BookmarkCheck size={14} className="text-amber-400" /> : <Bookmark size={14} />}
          </button>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground/50 hover:text-red-400"
            data-testid={`button-dismiss-opp-${opp.id}`}
            title="Dismiss"
          >
            <XCircle size={14} />
          </button>
        </div>
      </div>
      {(opp.sourceUrl || opp.url) && (
        <a href={opp.sourceUrl || opp.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] text-blue-400/70 hover:text-blue-400 transition-colors">
          <ExternalLink size={11} /> View opportunity
        </a>
      )}
    </div>
  );
}

function AuditTab({ workspaceId }: { workspaceId: number | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: auditData, isLoading, error } = useQuery<any>({
    queryKey: ["/api/workspaces", workspaceId, "social-audit", refreshKey],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/social-audit`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load audit");
      return res.json();
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 60,
  });

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
    queryClient.removeQueries({ queryKey: ["/api/workspaces", workspaceId, "social-audit"] });
  };

  if (isLoading) return (
    <div className="space-y-4" data-testid="audit-loading">
      {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl bg-muted/50" />)}
    </div>
  );

  if (error) return (
    <div className="bg-card/50 border border-border rounded-2xl p-8 flex flex-col items-center justify-center space-y-4 text-center" data-testid="audit-error">
      <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
        <BarChart2 size={22} className="text-red-400" />
      </div>
      <div>
        <h3 className="font-bold text-foreground text-base">Unable to load audit</h3>
        <p className="text-sm text-muted-foreground/70 mt-1 max-w-sm">Connect social accounts in Settings and try again.</p>
      </div>
      <a href="/settings" className="text-sm text-blue-400 hover:underline">Go to Settings → Connections</a>
    </div>
  );

  if (!auditData || !auditData.connectedPlatforms || auditData.connectedPlatforms.length === 0) return (
    <div className="bg-card/50 border border-border rounded-2xl p-8 flex flex-col items-center justify-center space-y-4 text-center" data-testid="audit-no-platforms">
      <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
        <BarChart2 size={22} className="text-blue-400" />
      </div>
      <div>
        <h3 className="font-bold text-foreground text-base">Connect your social accounts</h3>
        <p className="text-sm text-muted-foreground/70 mt-1 max-w-sm">Connect Meta, LinkedIn, or X in Settings to get a full social media health audit.</p>
      </div>
      <a href="/settings" className="text-sm text-blue-400 hover:underline">Go to Settings → Connections</a>
    </div>
  );

  const { metrics, aiReport, connectedPlatforms, generatedAt } = auditData;
  const score = aiReport?.healthScore ?? 0;
  const scoreColor = score >= 7 ? "text-green-400" : score >= 5 ? "text-yellow-400" : "text-red-400";
  const scoreRingColor = score >= 7 ? "stroke-green-500" : score >= 5 ? "stroke-yellow-500" : "stroke-red-500";

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-display font-bold text-foreground">Social Media Audit</h2>
          <div className="flex gap-1">
            {connectedPlatforms.map((p: string) => (
              <span key={p} className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full capitalize">{p}</span>
            ))}
          </div>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm" className="gap-2 text-xs" data-testid="button-refresh-audit">
          <RefreshCw size={12} /> Refresh
        </Button>
      </div>

      {aiReport && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card/50 border border-border rounded-2xl p-5 flex flex-col items-center justify-center" data-testid="audit-health-score">
            <div className="relative w-20 h-20 mb-2">
              <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/30" />
                <circle cx="18" cy="18" r="15.9155" fill="none" strokeWidth="3" strokeDasharray={`${score * 10}, 100`} strokeLinecap="round" className={scoreRingColor} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-display font-bold ${scoreColor}`}>{score}</span>
                <span className="text-[9px] text-muted-foreground/60">/10</span>
              </div>
            </div>
            <span className="text-xs text-muted-foreground font-semibold">Health Score</span>
          </div>

          {metrics && (
            <>
              <div className="bg-card/50 border border-border rounded-2xl p-4 space-y-1" data-testid="audit-fans">
                <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Followers</span>
                <p className="text-2xl font-display font-bold text-foreground">{(metrics.fans || 0).toLocaleString()}</p>
              </div>
              <div className="bg-card/50 border border-border rounded-2xl p-4 space-y-1" data-testid="audit-reach">
                <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Monthly Reach</span>
                <p className="text-2xl font-display font-bold text-foreground">{(metrics.reach || 0).toLocaleString()}</p>
              </div>
              <div className="bg-card/50 border border-border rounded-2xl p-4 space-y-1" data-testid="audit-engagement">
                <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Engaged Users</span>
                <p className="text-2xl font-display font-bold text-foreground">{(metrics.engagedUsers || 0).toLocaleString()}</p>
              </div>
            </>
          )}
        </div>
      )}

      {aiReport && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: "Summary", items: [aiReport.summary], icon: Activity, color: "blue" },
            { title: "Strengths", items: aiReport.strengths || [], icon: CheckCircle2, color: "green" },
            { title: "Improvements", items: aiReport.improvements || [], icon: AlertTriangle, color: "yellow" },
          ].map(({ title, items, icon: Icon, color }) => (
            <div key={title} className="bg-card/50 border border-border rounded-2xl p-4 space-y-3" data-testid={`audit-${title.toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <Icon size={14} className={`text-${color}-400`} />
                <h4 className="text-sm font-bold text-foreground">{title}</h4>
              </div>
              <ul className="space-y-2">
                {(Array.isArray(items) ? items : [items]).map((item: string, i: number) => (
                  <li key={i} className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                    <span className="mt-0.5">•</span><span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {aiReport?.actions?.length > 0 && (
        <div className="bg-card/50 border border-border rounded-2xl p-4 space-y-3" data-testid="audit-actions">
          <h4 className="text-sm font-bold text-foreground flex items-center gap-2"><Zap size={14} className="text-amber-400" /> Recommended Actions</h4>
          <ul className="space-y-2">
            {aiReport.actions.map((action: string, i: number) => (
              <li key={i} className="text-xs text-muted-foreground flex gap-2">
                <span className="text-amber-400 mt-0.5">{i + 1}.</span><span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {generatedAt && (
        <p className="text-[10px] text-muted-foreground/40 text-right">Last updated: {new Date(generatedAt).toLocaleString()}</p>
      )}
    </div>
  );
}


export default function Strategy() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [pageState, setPageState] = useState<PageState>("idle");
  const [businessUrl, setBusinessUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [structuredStrategy, setStructuredStrategy] = useState<StructuredStrategy | null>(null);
  const [activeTab, setActiveTab] = useState<StrategyTab>("overview");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [competitorName, setCompetitorName] = useState("");
  const [competitorUrl, setCompetitorUrl] = useState("");
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [showForceRefreshConfirm, setShowForceRefreshConfirm] = useState(false);
  const [isRunningAgents, setIsRunningAgents] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const lastPlanVersionRef = useRef<string | null>(null);
  const pendingSaveRef = useRef(false);
  const structuredStrategyRef = useRef<StructuredStrategy | null>(null);
  const [isRefining, setIsRefining] = useState(false);

  useEffect(() => {
    structuredStrategyRef.current = structuredStrategy;
  }, [structuredStrategy]);

  function loadPlan(plan: MarketingPlan) {
    setAnalysis({
      summary: plan.summary || "",
      competitors: Array.isArray(plan.competitors) ? plan.competitors.map((c: any) => typeof c === "string" ? c : c.name || JSON.stringify(c)) : [],
      swot: plan.swot || { strengths: [], weaknesses: [], opportunities: [], threats: [] },
      strategy: plan.strategy || "",
      sources: [],
    });
    if (plan.structuredStrategy) {
      setStructuredStrategy(plan.structuredStrategy as StructuredStrategy);
      lastPlanVersionRef.current = JSON.stringify(plan.structuredStrategy);
    }
    setActivePlanId(plan.id);
    setPageState("results");
  }

  const { data: plans, isLoading: loadingPlans } = useQuery<MarketingPlan[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "plans"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/plans`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch plans");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const hasActivePlanForRedirect = Array.isArray(plans) && plans.some((p: any) => p.status === "ACTIVE");
  useEffect(() => {
    if (!activeWorkspaceId || loadingPlans || !Array.isArray(plans)) return;
    if (!hasActivePlanForRedirect) {
      navigate("/launch");
    }
  }, [activeWorkspaceId, loadingPlans, hasActivePlanForRedirect]);

  const { data: workspace } = useQuery<any>({
    queryKey: ["/api/workspaces"],
    select: (workspaces: any[]) => workspaces?.find((ws: any) => ws.id === activeWorkspaceId),
    enabled: !!activeWorkspaceId,
  });

  const { data: performance, isLoading: loadingPerformance } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "performance"],
    enabled: !!activeWorkspaceId && activeTab === "analytics",
  });

  const { data: brandMonitors, isLoading: loadingMonitors } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "brand-monitors"],
    enabled: !!activeWorkspaceId && activeTab === "analytics",
  });

  // T025: Strategy revision history
  const { data: revisionData, isLoading: loadingRevisions } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "strategy-revisions"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/strategy-revisions`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch revisions");
      const data = await res.json();
      return { revisions: Array.isArray(data) ? data : [] };
    },
    enabled: !!activeWorkspaceId && activeTab === "revisions",
  });

  const revertRevisionMutation = useMutation({
    mutationFn: async (revisionId: number) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/strategy-revisions/${revisionId}/revert`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "strategy-revisions"] });
      toast({ title: "Strategy reverted", description: "Your strategy has been rolled back to this revision." });
    },
    onError: () => toast({ title: "Revert failed", description: "Could not revert to this revision.", variant: "destructive" }),
  });

  const analyzePerformance = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/performance/analyze`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "performance"] });
    },
  });

  const addCompetitor = useMutation({
    mutationFn: async (data: { name: string; url: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/brand-monitors`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-monitors"] });
      setCompetitorName("");
      setCompetitorUrl("");
    },
  });

  const analyzeCompetitor = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/brand-monitors/${id}/analyze`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-monitors"] });
    },
  });

  const deleteCompetitor = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/brand-monitors/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-monitors"] });
    },
  });

  const threatLevelColor = (level: string) => {
    switch (level?.toUpperCase()) {
      case "HIGH": return "bg-red-500/[0.1] text-red-400 border border-red-500/[0.15]";
      case "MEDIUM": return "bg-amber-500/[0.1] text-amber-400 border border-amber-500/[0.15]";
      case "LOW": return "bg-emerald-500/[0.1] text-emerald-400 border border-emerald-500/[0.15]";
      default: return "bg-muted/50 text-muted-foreground/70 border border-border";
    }
  };

  const [channelPrefs, setChannelPrefs] = useState<{ linkedin: boolean; x: boolean; meta: boolean; google: boolean }>({ linkedin: true, x: true, meta: true, google: true });

  useEffect(() => {
    if (workspace?.channelPreferences) {
      setChannelPrefs(workspace.channelPreferences);
    }
    if (workspace && !businessName && !businessUrl) {
      if (workspace.brandName || workspace.name) setBusinessName(workspace.brandName || workspace.name);
      if (workspace.url) setBusinessUrl(workspace.url);
    }
  }, [workspace]);

  useEffect(() => {
    if (plans && plans.length > 0 && pageState === "idle") {
      const latestPlan = plans[0];
      loadPlan(latestPlan);
    }
  }, [plans, loadingPlans]);

  useEffect(() => {
    if (plans && plans.length > 0 && pageState === "results") {
      const latestPlan = plans[0];
      if (latestPlan.structuredStrategy) {
        const planVersion = JSON.stringify(latestPlan.structuredStrategy);
        if (pendingSaveRef.current) {
          lastPlanVersionRef.current = planVersion;
          pendingSaveRef.current = false;
          return;
        }
        if (lastPlanVersionRef.current && lastPlanVersionRef.current !== planVersion) {
          setStructuredStrategy(latestPlan.structuredStrategy as StructuredStrategy);
          toast({ title: "Strategy Updated", description: "Your strategy plan has been updated by Draper." });
        }
        lastPlanVersionRef.current = planVersion;
      }
    }
  }, [plans, pageState]);

  const toggleChannelMutation = useMutation({
    mutationFn: async (newPrefs: typeof channelPrefs) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      const res = await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}`, { channelPreferences: newPrefs });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      toast({ title: "Channels Updated", description: "Your channel preferences have been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update channels.", variant: "destructive" });
    },
  });

  function handleToggleChannel(key: "linkedin" | "x" | "meta" | "google") {
    const newPrefs = { ...channelPrefs, [key]: !channelPrefs[key] };
    setChannelPrefs(newPrefs);
    toggleChannelMutation.mutate(newPrefs);
  }

  const refreshTrendsMutation = useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/refresh-trends`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.updatedStrategy) {
        setStructuredStrategy(data.updatedStrategy);
        lastPlanVersionRef.current = JSON.stringify(data.updatedStrategy);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      const trendCount = data.trendsFound?.length || 0;
      toast({ title: "Trends Refreshed", description: `Found ${trendCount} latest trends. Calendar topics updated.` });
    },
    onError: (err: any) => {
      toast({ title: "Refresh Failed", description: err.message || "Could not fetch latest trends.", variant: "destructive" });
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async (data: { url: string; businessName: string }) => {
      const res = await apiRequest("POST", "/api/ai/analyze", { ...data, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      setAnalysis({
        summary: data.summary || "",
        competitors: Array.isArray(data.competitors) ? data.competitors.map((c: any) => typeof c === "string" ? c : c.name || JSON.stringify(c)) : [],
        swot: data.swot || { strengths: [], weaknesses: [], opportunities: [], threats: [] },
        strategy: data.strategy || data.draftStrategy || "",
        sources: data.sources || [],
      });
      if (data.structuredStrategy) {
        setStructuredStrategy(data.structuredStrategy);
      }
      if (data.discoveredInsights) {
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      }
      setPageState("results");
    },
    onError: (err: any) => {
      setPageState("idle");
      toast({ title: "Analysis Failed", description: err.message || "Failed to analyze.", variant: "destructive" });
    },
  });

  const refineMutation = useMutation({
    mutationFn: async (data: { history: ChatMessage[]; currentStrategy: string; userFeedback: string }) => {
      setIsRefining(true);
      const res = await apiRequest("POST", "/api/ai/refine", { ...data, workspaceId: activeWorkspaceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      setIsRefining(false);
      const actions: ChatAction[] = data.actions || [];
      if (data.directionChanged) {
        actions.push({
          type: "ripple_offer",
          label: "Regenerate upcoming posts with new direction?",
          description: "Peggy will rewrite your next 14 days of draft and scheduled content with the updated tone.",
        });
      }
      setChatMessages((prev) => [...prev, { role: "assistant", content: data.response || "Done.", actions: actions.length > 0 ? actions : undefined }]);
      if (data.updatedStrategy && analysis) {
        setAnalysis({ ...analysis, strategy: data.updatedStrategy });
      }
    },
    onError: (err: any) => {
      setIsRefining(false);
      const msg = err?.message?.includes("402") ? "AI is not enabled for this workspace. Visit Settings → Billing to activate." : "Sorry, Draper ran into an issue refining your strategy.";
      setChatMessages((prev) => [...prev, { role: "assistant", content: msg }]);
      toast({ title: "Refine failed", description: msg, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatMessages, isRefining]);

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/agents/peggy/regenerate-upcoming`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Content Regenerated", description: `Peggy rewrote ${data.regenerated} upcoming posts with your new direction.` });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to regenerate content.", variant: "destructive" });
    },
  });

  const savePlanMutation = useMutation({
    mutationFn: async (plan: any) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/plans`, plan);
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data?.id) setActivePlanId(data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      toast({ title: "Plan Saved", description: "Your marketing plan has been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save plan.", variant: "destructive" });
    },
  });

  function handleAnalyze() {
    if (!businessUrl.trim() || !businessName.trim() || !activeWorkspaceId) return;
    setPageState("analyzing");
    analyzeMutation.mutate({ url: businessUrl, businessName });
  }

  function handleSendChat() {
    if (!chatInput.trim()) return;
    if (!activeWorkspaceId) {
      toast({ title: "Workspace not ready", description: "Please wait for your workspace to load, then try again.", variant: "destructive" });
      return;
    }
    const msg = chatInput.trim();
    const newMessages: ChatMessage[] = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(newMessages);
    setChatInput("");
    refineMutation.mutate({ history: newMessages, currentStrategy: analysis?.strategy || "", userFeedback: msg });
  }

  const updatePlanMutation = useMutation({
    mutationFn: async ({ planId, data }: { planId: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/plans/${planId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      toast({ title: "Plan Saved", description: "Your marketing plan has been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save plan.", variant: "destructive" });
    },
  });

  function handleSavePlan() {
    if (!analysis || !activeWorkspaceId) return;
    const planData = {
      summary: analysis.summary,
      competitors: analysis.competitors,
      swot: analysis.swot,
      strategy: analysis.strategy,
      structuredStrategy: structuredStrategy,
    };

    if (activePlanId) {
      updatePlanMutation.mutate({ planId: activePlanId, data: planData });
    } else {
      savePlanMutation.mutate({
        ...planData,
        workspaceId: activeWorkspaceId,
      });
    }
  }

  const generateCalendarMutation = useMutation({
    mutationFn: async () => {
      const planId = activePlanId || plans?.[0]?.id;
      const res = await apiRequest("POST", "/api/content/generate-calendar", {
        workspaceId: activeWorkspaceId,
        marketingPlanId: planId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      if (data.created === 0) {
        toast({ title: "Already up to date", description: "No new posts created — existing scheduled items were kept. View them in Content Studio → Calendar tab.", variant: "default" });
      } else {
        toast({ title: `${data.created} Posts Scheduled`, description: "Your calendar is ready. Head to Content Studio → Calendar tab to review.", variant: "default" });
        setTimeout(() => navigate('/content'), 1500);
      }
    },
    onError: (err: any) => {
      toast({ title: "Calendar Generation Failed", description: err.message || "Could not generate calendar", variant: "destructive" });
    },
  });

  const updateStrategyMutation = useMutation({
    mutationFn: async (updatedStrategy: StructuredStrategy) => {
      const planId = activePlanId || plans?.[0]?.id;
      if (!planId) throw new Error("No plan to update");
      const res = await apiRequest("PUT", `/api/plans/${planId}`, { structuredStrategy: updatedStrategy });
      return res.json();
    },
    onSuccess: () => {
      pendingSaveRef.current = true;
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      toast({ title: "Saved", description: "Your changes have been saved." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save strategy changes.", variant: "destructive" });
    },
  });

  const handlePlatformUpdate = useCallback((updatedPlan: PlatformPlan) => {
    const current = structuredStrategyRef.current;
    if (!current) return;
    const newPlatforms = (current.platforms || []).map(p =>
      (p.platform ?? "").toLowerCase() === (updatedPlan.platform ?? "").toLowerCase() ? updatedPlan : p
    );
    const newStrategy = { ...current, platforms: newPlatforms };
    structuredStrategyRef.current = newStrategy;
    setStructuredStrategy(newStrategy);
    lastPlanVersionRef.current = JSON.stringify(newStrategy);
    updateStrategyMutation.mutate(newStrategy);
  }, [activePlanId]);

  const handleOverviewUpdate = useCallback((field: string, value: any) => {
    if (!structuredStrategy) return;
    const newStrategy = { ...structuredStrategy, [field]: value };
    setStructuredStrategy(newStrategy);
    lastPlanVersionRef.current = JSON.stringify(newStrategy);
    updateStrategyMutation.mutate(newStrategy);
  }, [structuredStrategy, activePlanId]);

  const handleAdPlanUpdate = useCallback((index: number, updated: AdPlan) => {
    if (!structuredStrategy) return;
    const newAdPlans = [...(structuredStrategy.adPlans || [])];
    newAdPlans[index] = updated;
    const newStrategy = { ...structuredStrategy, adPlans: newAdPlans };
    setStructuredStrategy(newStrategy);
    lastPlanVersionRef.current = JSON.stringify(newStrategy);
    updateStrategyMutation.mutate(newStrategy);
  }, [structuredStrategy, activePlanId]);

  const handleLeadPlanUpdate = useCallback((updated: LeadPlan) => {
    if (!structuredStrategy) return;
    const newStrategy = { ...structuredStrategy, leadPlan: updated };
    setStructuredStrategy(newStrategy);
    lastPlanVersionRef.current = JSON.stringify(newStrategy);
    updateStrategyMutation.mutate(newStrategy);
  }, [structuredStrategy, activePlanId]);

  const handleOffersUpdate = useCallback((updated: { aggressive: MarketingOffer[]; medium: MarketingOffer[]; general: MarketingOffer[] }) => {
    if (!structuredStrategy) return;
    const newStrategy = { ...structuredStrategy, marketingOffers: updated };
    setStructuredStrategy(newStrategy);
    lastPlanVersionRef.current = JSON.stringify(newStrategy);
    updateStrategyMutation.mutate(newStrategy);
  }, [structuredStrategy, activePlanId]);

  const runAgentsMutation = useMutation({
    mutationFn: async (forceRefresh?: boolean) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      const body: any = {};
      if (forceRefresh) body.forceStrategyRefresh = true;
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate`, body);
      return res.json();
    },
    onSuccess: (data: any) => {
      setIsRunningAgents(false);
      setShowForceRefreshConfirm(false);
      toast({ title: "Agents running", description: "Watch the agent stream for live updates. Your strategy and content will update automatically." });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
    },
    onError: (err: any) => {
      setIsRunningAgents(false);
      setShowForceRefreshConfirm(false);
      toast({ title: "Failed to start agents", description: err.message || "Could not start orchestration.", variant: "destructive" });
    },
  });

  const activePlan = plans?.find((p: any) => p.status === "ACTIVE");
  const activePlanStructured = activePlan?.structuredStrategy as any;
  const coreSection = activePlanStructured?.core;
  const trendsSection = activePlanStructured?.trends;
  const coreLocked = !!(coreSection?.lockedAt);
  const lastTrendsRefresh = trendsSection?.lastRefreshedAt;

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <Compass size={48} className="text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Select a workspace to begin strategy analysis.</p>
      </div>
    );
  }

  const getPlatformPlan = (platform: string): PlatformPlan | undefined =>
    structuredStrategy?.platforms?.find((p) => (p.platform ?? "").toLowerCase() === (platform ?? "").toLowerCase());

  return (
    <div className="space-y-6" data-testid="strategy-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">Strategy Hub</h1>
        <p className="text-muted-foreground mt-1 text-sm">Agent Draper — Market analysis and strategic planning powered by Google Search.</p>
      </div>

      <PlaybookCard workspaceId={activeWorkspaceId} />

      <ContentMatrixCard workspaceId={activeWorkspaceId} />

      {pageState === "idle" && loadingPlans && (
        <div className="flex items-center gap-2 text-muted-foreground/70 text-sm py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading strategy...
        </div>
      )}

      {pageState === "idle" && !loadingPlans && (
        <div className="max-w-2xl space-y-6" data-testid="strategy-form">
          {(!plans || plans.length === 0) && (
            <div className="bg-gradient-to-r from-blue-500/[0.06] via-purple-500/[0.03] to-transparent border border-blue-500/[0.1] rounded-2xl p-5 space-y-3" data-testid="strategy-explainer">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-blue-400" />
                <h3 className="text-sm font-display font-bold text-foreground">What Draper Will Do</h3>
              </div>
              <p className="text-sm text-muted-foreground/80">
                Draper will analyze your market, discover competitors, build a SWOT analysis, and create a per-channel content plan. This takes about 60 seconds.
              </p>
              <details className="group" data-testid="why-do-this-section">
                <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 transition-colors font-medium flex items-center gap-1">
                  <ChevronRight size={12} className="group-open:rotate-90 transition-transform" />
                  Why do this?
                </summary>
                <div className="mt-2 text-xs text-muted-foreground/70 space-y-1.5 pl-4">
                  <p>A strategy analysis gives your AI agents direction. Without it, content is generic.</p>
                  <p>After analysis, Draper identifies your competitive advantages, target audience messaging, and the best channels for your business.</p>
                  <p>Peggy and Pete then use this strategy to create on-brand, targeted content automatically.</p>
                </div>
              </details>
            </div>
          )}
          <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Search size={18} className="text-blue-400" />
              <h2 className="text-lg font-display font-bold text-foreground">Analyze Your Market</h2>
            </div>
            <p className="text-muted-foreground text-sm">Enter your business details and Draper will research your market using Google Search grounding.</p>
            <div className="space-y-3">
              <input
                data-testid="input-business-name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Business name"
                className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
              />
              <input
                data-testid="input-business-url"
                value={businessUrl}
                onChange={(e) => setBusinessUrl(e.target.value)}
                placeholder="Business URL (e.g. https://example.com)"
                className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70"
                onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
              />
            </div>
            <button
              data-testid="button-analyze"
              onClick={handleAnalyze}
              disabled={!businessUrl.trim() || !businessName.trim()}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl px-6 py-3 text-sm flex items-center gap-2 transition-colors"
            >
              <Compass size={16} />
              Run Analysis
            </button>
          </div>

          {loadingPlans ? (
            <div className="flex items-center gap-2 text-muted-foreground/70 text-sm">
              <Loader2 size={16} className="animate-spin" /> Loading saved plans...
            </div>
          ) : plans && plans.length > 0 ? (
            <div className="space-y-3" data-testid="saved-plans">
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Saved Plans</h3>
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-card/50 border border-border rounded-xl p-4 cursor-pointer hover:border-border transition-colors"
                  data-testid={`plan-${plan.id}`}
                  onClick={() => loadPlan(plan)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-foreground truncate flex-1">{plan.summary || "Untitled Plan"}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase ${
                      plan.status === "ACTIVE" ? "bg-green-500/10 text-green-400" : "bg-muted/50 text-muted-foreground"
                    }`}>
                      {plan.status}
                    </span>
                  </div>
                  <span className="text-[10px] text-muted-foreground/50">
                    {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {pageState === "analyzing" && (
        <div className="flex flex-col items-center justify-center py-20" data-testid="analyzing-state">
          <Loader2 size={40} className="animate-spin text-blue-500 mb-4" />
          <p className="text-muted-foreground font-medium">Draper is analyzing your market...</p>
          <p className="text-muted-foreground/70 text-sm mt-1">Researching competitors, building SWOT analysis, and creating per-channel strategy plans.</p>
          <p className="text-muted-foreground/50 text-xs mt-3">This may take 30-60 seconds for a comprehensive multi-platform plan.</p>
        </div>
      )}

      {(pageState === "results" || pageState === "chatting") && analysis && (
        <div className="space-y-6" data-testid="analysis-results">
          {showForceRefreshConfirm && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3" data-testid="force-refresh-confirm">
              <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-semibold text-foreground">Rebuild Brand Strategy?</p>
                <p className="text-xs text-muted-foreground">This will rebuild your brand positioning and audience. Your current core brand foundation will be replaced. Are you sure?</p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    data-testid="button-confirm-force-refresh"
                    onClick={() => { setIsRunningAgents(true); runAgentsMutation.mutate(true); }}
                    disabled={runAgentsMutation.isPending}
                    className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-lg px-4 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    {runAgentsMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
                    Yes, Rebuild
                  </button>
                  <button
                    data-testid="button-cancel-force-refresh"
                    onClick={() => setShowForceRefreshConfirm(false)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="button-run-agents"
              onClick={() => { setIsRunningAgents(true); runAgentsMutation.mutate(false); }}
              disabled={runAgentsMutation.isPending || isRunningAgents}
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {(runAgentsMutation.isPending || isRunningAgents) ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              {(runAgentsMutation.isPending || isRunningAgents) ? "Running..." : "Run Agents"}
            </button>
            {activePlan && (
              <button
                data-testid="button-refresh-brand-strategy"
                onClick={() => setShowForceRefreshConfirm(true)}
                disabled={runAgentsMutation.isPending || isRunningAgents}
                className="border border-border hover:border-amber-500/40 text-muted-foreground hover:text-amber-400 font-bold rounded-xl px-4 py-2.5 text-xs flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} />
                Refresh Brand Strategy
              </button>
            )}
            <button
              data-testid="button-save-plan"
              onClick={handleSavePlan}
              disabled={savePlanMutation.isPending || updatePlanMutation.isPending}
              className="border border-border hover:border-border/70 text-muted-foreground hover:text-foreground font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {(savePlanMutation.isPending || updatePlanMutation.isPending) ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Plan
            </button>
            <button
              data-testid="button-launch-strategy"
              onClick={() => navigate("/launch")}
              className="border border-emerald-500/20 hover:border-emerald-500/40 text-emerald-400 font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
              title="Advanced multi-step guided session for detailed strategic planning — not required for basic use"
            >
              <Rocket size={14} /> Strategy Wizard
            </button>
            {structuredStrategy && (
              <>
              <button
                data-testid="button-generate-calendar"
                onClick={() => generateCalendarMutation.mutate()}
                disabled={generateCalendarMutation.isPending}
                className="border border-border hover:border-border/70 text-muted-foreground hover:text-foreground font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {generateCalendarMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
                {generateCalendarMutation.isPending ? "Generating..." : "Generate Content"}
              </button>
              <button
                data-testid="button-refresh-trends"
                onClick={() => refreshTrendsMutation.mutate()}
                disabled={refreshTrendsMutation.isPending}
                className="border border-blue-500/20 hover:border-blue-500/40 text-blue-400 font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {refreshTrendsMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Refresh Trends
              </button>
              <button
                data-testid="button-refine"
                onClick={() => setPageState("chatting")}
                className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-bold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
              >
                Refine <ArrowRight size={14} />
              </button>
              </>
            )}
            <button
              data-testid="button-new-analysis"
              onClick={() => { setPageState("idle"); setAnalysis(null); setStructuredStrategy(null); setChatMessages([]); setActivePlanId(null); }}
              className="text-muted-foreground/70 hover:text-foreground text-sm font-medium transition-colors flex items-center gap-1"
            >
              <RotateCcw size={14} /> New Analysis
            </button>
          </div>

          {!structuredStrategy && (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="strategy-not-generated">
              <Compass size={40} className="text-muted-foreground/30 mb-4" />
              <h2 className="text-lg font-display font-bold text-foreground mb-2">Strategy Not Yet Generated</h2>
              <p className="text-muted-foreground/70 text-sm max-w-md mb-6">Your market analysis is complete, but no structured strategy plan has been created yet. Run a new analysis to generate a full per-channel strategy.</p>
              <button
                data-testid="button-run-new-analysis"
                onClick={() => { setPageState("idle"); setAnalysis(null); setStructuredStrategy(null); setChatMessages([]); setActivePlanId(null); }}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl px-6 py-3 text-sm flex items-center gap-2 transition-colors"
              >
                <Compass size={16} />
                Run New Analysis
              </button>
            </div>
          )}

          {structuredStrategy && (
            <div className="space-y-4">
              <div className="flex items-center gap-1 overflow-x-auto pb-2 custom-scrollbar" data-testid="strategy-tabs">
                {TAB_CONFIG.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`tab-${tab.key}`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-colors shrink-0 ${
                        activeTab === tab.key
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "text-muted-foreground hover:text-foreground border border-transparent"
                      }`}
                    >
                      <Icon size={14} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === "overview" && (() => {
                try {
                  const overview = structuredStrategy?.overview || analysis?.summary || "";
                  const goals = Array.isArray(structuredStrategy?.goals) ? structuredStrategy!.goals : [];
                  const timeline = structuredStrategy?.timeline ?? "";
                  const platforms = Array.isArray(structuredStrategy?.platforms) ? structuredStrategy!.platforms : [];
                  const swot = analysis?.swot ?? { strengths: [], weaknesses: [], opportunities: [], threats: [] };
                  return (
                <div className="space-y-6">
                  {coreSection && (
                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 space-y-3" data-testid="brand-foundation-section">
                      <div className="flex items-center gap-2">
                        <Lock size={14} className="text-blue-400" />
                        <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider">Your Brand Foundation</h3>
                        <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full font-bold ml-1">LOCKED</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-xs">
                        {coreSection.positioning && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Positioning</span>
                            <p className="text-foreground/80 mt-0.5">{coreSection.positioning}</p>
                          </div>
                        )}
                        {coreSection.targetAudience && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Target Audience</span>
                            <p className="text-foreground/80 mt-0.5">{coreSection.targetAudience}</p>
                          </div>
                        )}
                        {coreSection.brandVoice && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Brand Voice</span>
                            <p className="text-foreground/80 mt-0.5">{coreSection.brandVoice}</p>
                          </div>
                        )}
                        {Array.isArray(coreSection.usps) && coreSection.usps.length > 0 && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Unique Value Props</span>
                            <ul className="mt-0.5 space-y-0.5">
                              {coreSection.usps.slice(0, 3).map((usp: string, i: number) => (
                                <li key={i} className="text-foreground/80 flex items-start gap-1"><CheckCircle2 size={10} className="text-blue-400 mt-0.5 shrink-0" />{usp}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      {coreSection.lockedAt && (
                        <p className="text-[10px] text-muted-foreground/50">Locked on {new Date(coreSection.lockedAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  )}
                  {trendsSection && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 space-y-3" data-testid="current-opportunities-section">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-emerald-400" />
                        <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wider">Current Opportunities</h3>
                        {lastTrendsRefresh && (
                          <span className="text-[10px] text-muted-foreground/50 ml-1">Updated {new Date(lastTrendsRefresh).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-xs">
                        {Array.isArray(trendsSection.currentOpportunities) && trendsSection.currentOpportunities.length > 0 && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Opportunities</span>
                            <ul className="mt-0.5 space-y-0.5">
                              {trendsSection.currentOpportunities.slice(0, 3).map((op: string, i: number) => (
                                <li key={i} className="text-foreground/80 flex items-start gap-1"><TrendingUp size={10} className="text-emerald-400 mt-0.5 shrink-0" />{op}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {Array.isArray(trendsSection.seasonalAngles) && trendsSection.seasonalAngles.length > 0 && (
                          <div>
                            <span className="text-muted-foreground/60 uppercase font-bold text-[10px]">Seasonal Angles</span>
                            <ul className="mt-0.5 space-y-0.5">
                              {trendsSection.seasonalAngles.slice(0, 3).map((angle: string, i: number) => (
                                <li key={i} className="text-foreground/80 flex items-start gap-1"><Radar size={10} className="text-emerald-400 mt-0.5 shrink-0" />{angle}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="bg-card/50 border border-border rounded-xl p-5 space-y-3">
                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Strategy Overview</h3>
                    <EditableField
                      value={overview}
                      onSave={(v) => handleOverviewUpdate("overview", v)}
                      className="text-sm text-foreground/80 leading-relaxed"
                      multiline
                    />
                    {goals.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <span className="text-xs text-muted-foreground/70 uppercase font-bold">Goals</span>
                        <ul className="space-y-1.5">
                          {goals.map((g, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <Target size={12} className="text-blue-400 mt-0.5 shrink-0" />
                              <EditableField
                                value={g}
                                onSave={(v) => {
                                  const newGoals = [...goals];
                                  newGoals[i] = v;
                                  handleOverviewUpdate("goals", newGoals);
                                }}
                                className="flex-1"
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {timeline && (
                      <div className="mt-4">
                        <span className="text-xs text-muted-foreground/70 uppercase font-bold">Timeline</span>
                        <EditableField
                          value={timeline}
                          onSave={(v) => handleOverviewUpdate("timeline", v)}
                          className="text-sm text-muted-foreground mt-1"
                        />
                      </div>
                    )}
                  </div>

                  {platforms.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {platforms.map((p) => (
                      <div
                        key={p.platform ?? "unknown"}
                        className="bg-card/50 border border-border rounded-xl p-4 cursor-pointer hover:border-blue-500/20 transition-colors"
                        onClick={() => setActiveTab("channels")}
                        data-testid={`platform-card-${(p.platform ?? "").toLowerCase()}`}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-sm font-bold text-foreground">{p.platform ?? "Unknown"}</h4>
                          <span className="text-[10px] text-muted-foreground/70">{p.postingFrequency ?? ""}</span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{p.objective ?? ""}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(p.contentTypes ?? []).slice(0, 3).map((ct, i) => (
                            <span key={i} className="text-[10px] bg-muted/50 text-muted-foreground px-2 py-0.5 rounded">{ct}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}

                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-3" data-testid="summary-section">
                      <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Market Summary</h3>
                      <p className="text-sm text-foreground/80 leading-relaxed">{analysis?.summary ?? ""}</p>
                    </div>

                    <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-3" data-testid="competitors-section">
                      <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Competitors</h3>
                      <div className="space-y-2">
                        {(analysis?.competitors ?? []).map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-foreground/70">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                            <span>{c}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4 lg:col-span-2" data-testid="swot-section">
                      <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">SWOT Analysis</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(["strengths", "weaknesses", "opportunities", "threats"] as const).map((key) => {
                          const colors: Record<string, string> = {
                            strengths: "bg-green-500/10 text-green-400 border-green-500/20",
                            weaknesses: "bg-red-500/10 text-red-400 border-red-500/20",
                            opportunities: "bg-blue-500/10 text-blue-400 border-blue-500/20",
                            threats: "bg-amber-500/10 text-amber-400 border-amber-500/20",
                          };
                          return (
                            <div key={key} className={`rounded-xl border p-4 space-y-2 ${colors[key]}`}>
                              <span className="text-xs font-bold uppercase">{key}</span>
                              <ul className="space-y-1">
                                {(swot?.[key] ?? []).map((item, i) => (
                                  <li key={i} className="text-sm opacity-80 flex items-start gap-2">
                                    <div className="w-1 h-1 rounded-full bg-current mt-2 shrink-0" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-xl bg-card/50 border border-border" data-testid="brand-style-badge">
                    <span className="text-sm text-muted-foreground font-medium">Brand Style:</span>
                    {workspace?.preferredContentStyle ? (
                      <>
                        <span className="text-sm font-semibold text-foreground capitalize">{workspace.preferredContentStyle}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <a
                          href="/deep-plan"
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                          data-testid="link-change-brand-style"
                        >
                          Change →
                        </a>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-muted-foreground/60">No style set yet</span>
                        <span className="text-muted-foreground/50">•</span>
                        <a
                          href="/deep-plan"
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                          data-testid="link-set-brand-style"
                        >
                          Set one →
                        </a>
                      </>
                    )}
                  </div>
                </div>
                  );
                } catch (err) {
                  console.error("Strategy overview render error:", err);
                  return (
                    <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="strategy-render-error">
                      <Compass size={32} className="text-muted-foreground/30 mb-3" />
                      <p className="text-sm text-muted-foreground">Could not display strategy overview. Try running a new analysis.</p>
                    </div>
                  );
                }
              })()}

              {activeTab === "channels" && (
                structuredStrategy
                  ? (() => {
                      try {
                        const safePlatforms = Array.isArray(structuredStrategy?.platforms) ? structuredStrategy.platforms : [];
                        const safeStrategy = { ...structuredStrategy, platforms: safePlatforms };
                        return <ChannelsView
                          structuredStrategy={safeStrategy}
                          channelPrefs={channelPrefs}
                          handleToggleChannel={handleToggleChannel}
                          handlePlatformUpdate={handlePlatformUpdate}
                          getPlatformPlan={getPlatformPlan}
                        />;
                      } catch (err) {
                        console.error("Channels tab render error:", err);
                        return (
                          <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="channels-render-error">
                            <Compass size={32} className="text-muted-foreground/30 mb-3" />
                            <p className="text-sm text-muted-foreground">Could not display channels. Try running a new analysis.</p>
                          </div>
                        );
                      }
                    })()
                  : <div className="flex items-center gap-3 p-8 text-muted-foreground text-sm"><Loader2 size={16} className="animate-spin" /> Loading channels...</div>
              )}

              {activeTab === "campaign-hub" && (
                structuredStrategy
                  ? (() => {
                      try {
                        const safeAdPlans = Array.isArray(structuredStrategy?.adPlans) ? structuredStrategy.adPlans : [];
                        const safeStrategy = { ...structuredStrategy, adPlans: safeAdPlans };
                        return <CampaignHubView
                          structuredStrategy={safeStrategy}
                          workspaceId={activeWorkspaceId!}
                          handleAdPlanUpdate={handleAdPlanUpdate}
                          handleLeadPlanUpdate={handleLeadPlanUpdate}
                          handleOffersUpdate={handleOffersUpdate}
                        />;
                      } catch (err) {
                        console.error("Campaign hub render error:", err);
                        return (
                          <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="campaign-hub-render-error">
                            <Compass size={32} className="text-muted-foreground/30 mb-3" />
                            <p className="text-sm text-muted-foreground">Could not display campaign hub. Try running a new analysis.</p>
                          </div>
                        );
                      }
                    })()
                  : <div className="flex items-center gap-3 p-8 text-muted-foreground text-sm"><Loader2 size={16} className="animate-spin" /> Loading campaign hub...</div>
              )}

              {activeTab === "radar" && (
                <RadarInlineView workspaceId={activeWorkspaceId!} />
              )}

              {activeTab === "analytics" && (
                <div className="space-y-8" data-testid="analytics-tab-content">
                  <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-6">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/[0.1] flex items-center justify-center">
                          <BrainCircuit size={15} className="text-blue-400" />
                        </div>
                        <h2 className="text-lg font-display font-bold text-foreground">AI Performance Analysis</h2>
                      </div>
                      <Button
                        data-testid="button-run-draper-analytics"
                        onClick={() => analyzePerformance.mutate()}
                        disabled={analyzePerformance.isPending}
                        className="bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        {analyzePerformance.isPending ? (
                          <Loader2 size={16} className="animate-spin mr-2" />
                        ) : (
                          <Zap size={16} className="mr-2" />
                        )}
                        Run Draper Analytics
                      </Button>
                    </div>

                    {analyzePerformance.isPending && (
                      <div className="space-y-3">
                        <Skeleton className="h-5 w-full bg-muted/50 rounded-lg" />
                        <Skeleton className="h-5 w-3/4 bg-muted/50 rounded-lg" />
                        <Skeleton className="h-5 w-1/2 bg-muted/50 rounded-lg" />
                      </div>
                    )}

                    {analyzePerformance.data && (
                      <div className="space-y-4">
                        {analyzePerformance.data.insights && (
                          <div className="bg-card/50 border border-border rounded-xl p-4">
                            <p className="text-sm text-foreground/70 leading-relaxed">{analyzePerformance.data.insights}</p>
                          </div>
                        )}
                        {analyzePerformance.data.recommendations && analyzePerformance.data.recommendations.length > 0 && (
                          <div className="space-y-2">
                            <span className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider">Recommendations</span>
                            {analyzePerformance.data.recommendations.map((rec: string, i: number) => (
                              <div key={i} className="flex items-start gap-3 bg-muted/30 border border-border rounded-xl px-4 py-3">
                                <TrendingUp size={14} className="text-blue-400 mt-0.5 shrink-0" />
                                <p className="text-sm text-muted-foreground">{rec}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {loadingPerformance ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {Array(2).fill(0).map((_, i) => (
                        <Skeleton key={i} className="h-48 w-full bg-card/50 rounded-2xl" />
                      ))}
                    </div>
                  ) : performance && performance.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {performance.map((snapshot: any) => (
                        <div
                          key={snapshot.id}
                          className="bg-card/50 border border-border rounded-2xl p-5 space-y-4"
                          data-testid={`performance-card-${snapshot.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/[0.12] flex items-center justify-center">
                              <BarChart3 size={15} className="text-purple-400" />
                            </div>
                            <h3 className="text-base font-display font-bold text-foreground">{snapshot.agentName || snapshot.agent || "Agent"}</h3>
                          </div>

                          {snapshot.metrics && typeof snapshot.metrics === "object" && (
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(snapshot.metrics).map(([key, val]) => (
                                <div key={key} className="bg-muted/30 border border-border rounded-lg px-3 py-2">
                                  <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider block">{key}</span>
                                  <span className="text-sm font-bold text-foreground/80 font-mono">{String(val)}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {snapshot.insights && (
                            <p className="text-sm text-muted-foreground leading-relaxed">{snapshot.insights}</p>
                          )}

                          {snapshot.recommendations && snapshot.recommendations.length > 0 && (
                            <div className="space-y-1.5">
                              {snapshot.recommendations.map((rec: string, i: number) => (
                                <div key={i} className="flex items-start gap-2">
                                  <div className="w-1 h-1 rounded-full bg-blue-400 mt-2 shrink-0" />
                                  <p className="text-xs text-muted-foreground">{rec}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/[0.1] flex items-center justify-center">
                        <Shield size={15} className="text-emerald-400" />
                      </div>
                      <h2 className="text-lg font-display font-bold text-foreground">Brand Monitor</h2>
                    </div>

                    <div className="flex items-end gap-3 flex-wrap">
                      <div className="flex-1 min-w-0 sm:min-w-[140px]">
                        <label className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider block mb-1.5">Competitor Name</label>
                        <input
                          type="text"
                          value={competitorName}
                          onChange={(e) => setCompetitorName(e.target.value)}
                          placeholder="Competitor name"
                          className="w-full bg-card/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500/50"
                          data-testid="input-competitor-name"
                        />
                      </div>
                      <div className="flex-1 min-w-0 sm:min-w-[200px]">
                        <label className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider block mb-1.5">URL</label>
                        <input
                          type="text"
                          value={competitorUrl}
                          onChange={(e) => setCompetitorUrl(e.target.value)}
                          placeholder="https://competitor.com"
                          className="w-full bg-card/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500/50"
                          data-testid="input-competitor-url"
                        />
                      </div>
                      <Button
                        data-testid="button-add-competitor"
                        onClick={() => {
                          if (competitorName.trim() && competitorUrl.trim()) {
                            addCompetitor.mutate({ name: competitorName.trim(), url: competitorUrl.trim() });
                          }
                        }}
                        disabled={addCompetitor.isPending || !competitorName.trim() || !competitorUrl.trim()}
                        className="bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        {addCompetitor.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                        Add Competitor
                      </Button>
                    </div>

                    {loadingMonitors ? (
                      <div className="space-y-3">
                        {Array(2).fill(0).map((_, i) => (
                          <Skeleton key={i} className="h-24 w-full bg-card/50 rounded-xl" />
                        ))}
                      </div>
                    ) : brandMonitors && brandMonitors.length > 0 ? (
                      <div className="space-y-3">
                        {brandMonitors.map((monitor: any) => (
                          <div
                            key={monitor.id}
                            className="bg-muted/30 border border-border rounded-xl p-4 space-y-3"
                            data-testid={`competitor-card-${monitor.id}`}
                          >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0">
                                <h3 className="text-sm font-semibold text-foreground/90">{monitor.name}</h3>
                                {monitor.url && (
                                  <a href={monitor.url} target="_blank" rel="noopener noreferrer" className="text-blue-400/60 shrink-0">
                                    <ExternalLink size={13} />
                                  </a>
                                )}
                                {monitor.threatLevel && (
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide ${threatLevelColor(monitor.threatLevel)}`}>
                                    {monitor.threatLevel}
                                  </span>
                                )}
                                {monitor.lastAnalysis?.sentiment && (
                                  <span className="text-[10px] text-muted-foreground/70 font-medium">
                                    Sentiment: {monitor.lastAnalysis.sentiment}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  data-testid={`button-analyze-competitor-${monitor.id}`}
                                  onClick={() => analyzeCompetitor.mutate(monitor.id)}
                                  disabled={analyzeCompetitor.isPending}
                                  className="bg-muted hover:bg-muted text-foreground/70 text-xs"
                                >
                                  {analyzeCompetitor.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Search size={13} className="mr-1.5" />}
                                  Analyze
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => deleteCompetitor.mutate(monitor.id)}
                                  disabled={deleteCompetitor.isPending}
                                  data-testid={`button-delete-competitor-${monitor.id}`}
                                >
                                  <Trash2 size={14} className="text-muted-foreground/70" />
                                </Button>
                              </div>
                            </div>

                            {monitor.lastAnalysis && (
                              <div className="grid md:grid-cols-2 gap-2 mt-2">
                                {monitor.lastAnalysis.strengths && monitor.lastAnalysis.strengths.length > 0 && (
                                  <div className="bg-muted/30 border border-border rounded-lg p-3">
                                    <span className="text-[10px] font-semibold text-emerald-400/60 uppercase tracking-wider block mb-1.5">Strengths</span>
                                    {monitor.lastAnalysis.strengths.map((s: string, i: number) => (
                                      <p key={i} className="text-xs text-muted-foreground leading-relaxed">{s}</p>
                                    ))}
                                  </div>
                                )}
                                {monitor.lastAnalysis.weaknesses && monitor.lastAnalysis.weaknesses.length > 0 && (
                                  <div className="bg-muted/30 border border-border rounded-lg p-3">
                                    <span className="text-[10px] font-semibold text-red-400/60 uppercase tracking-wider block mb-1.5">Weaknesses</span>
                                    {monitor.lastAnalysis.weaknesses.map((w: string, i: number) => (
                                      <p key={i} className="text-xs text-muted-foreground leading-relaxed">{w}</p>
                                    ))}
                                  </div>
                                )}
                                {monitor.lastAnalysis.recentMoves && monitor.lastAnalysis.recentMoves.length > 0 && (
                                  <div className="bg-muted/30 border border-border rounded-lg p-3">
                                    <span className="text-[10px] font-semibold text-amber-400/60 uppercase tracking-wider block mb-1.5">Recent Moves</span>
                                    {monitor.lastAnalysis.recentMoves.map((m: string, i: number) => (
                                      <p key={i} className="text-xs text-muted-foreground leading-relaxed">{m}</p>
                                    ))}
                                  </div>
                                )}
                                {monitor.lastAnalysis.recommendations && monitor.lastAnalysis.recommendations.length > 0 && (
                                  <div className="bg-muted/30 border border-border rounded-lg p-3">
                                    <span className="text-[10px] font-semibold text-blue-400/60 uppercase tracking-wider block mb-1.5">Recommendations</span>
                                    {monitor.lastAnalysis.recommendations.map((r: string, i: number) => (
                                      <p key={i} className="text-xs text-muted-foreground leading-relaxed">{r}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Shield size={24} className="text-muted-foreground/30 mb-3" />
                        <p className="text-muted-foreground/60 text-sm">No competitors being tracked yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "audit" && (
            <AuditTab workspaceId={activeWorkspaceId} />
          )}

          {activeTab === "revisions" && (
            <div className="space-y-4" data-testid="revision-history-panel">
              <div className="flex items-center gap-3">
                <History size={16} className="text-blue-400" />
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Strategy Revision History</h3>
              </div>
              <p className="text-xs text-muted-foreground">Draper automatically reviews and updates your strategy based on engagement data. Each revision is saved here so you can review changes and revert if needed.</p>

              {loadingRevisions && (
                <div className="space-y-3">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
                </div>
              )}

              {!loadingRevisions && (!revisionData?.revisions || revisionData.revisions.length === 0) && (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="no-revisions-message">
                  <History size={32} className="text-muted-foreground/30 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground mb-1">No revision history yet</p>
                  <p className="text-xs text-muted-foreground/60">Draper will automatically review your strategy weekly and save revisions here.</p>
                </div>
              )}

              {!loadingRevisions && revisionData?.revisions && revisionData.revisions.length > 0 && (
                <div className="space-y-4">
                  {revisionData.revisions.map((rev: any, idx: number) => (
                    <div
                      key={rev.id}
                      className={`bg-card/50 border rounded-xl p-5 space-y-3 ${idx === 0 ? "border-blue-500/30" : "border-border"}`}
                      data-testid={`revision-card-${rev.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-[10px] font-bold bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full uppercase tracking-wide">Latest</span>}
                          <span className="text-xs text-muted-foreground">
                            {new Date(rev.savedAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })} at {new Date(rev.savedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 gap-1 shrink-0"
                          onClick={() => revertRevisionMutation.mutate(rev.id)}
                          disabled={revertRevisionMutation.isPending}
                          data-testid={`revert-revision-${rev.id}`}
                        >
                          <RotateCcw size={11} />
                          Revert to this
                        </Button>
                      </div>

                      {rev.changeSummary && (
                        <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3">
                          <p className="text-xs font-semibold text-blue-400 mb-1 flex items-center gap-1.5">
                            <BrainCircuit size={11} /> Draper's Summary
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed" data-testid={`revision-summary-${rev.id}`}>{rev.changeSummary}</p>
                        </div>
                      )}

                      {rev.diff && Array.isArray(rev.diff) && rev.diff.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                            <GitCompare size={11} /> Changes
                          </p>
                          <div className="space-y-1.5">
                            {(rev.diff as Array<{ field: string; oldValue: string; newValue: string }>).map((change) => (
                              <div key={change.field} className="rounded-lg bg-muted/30 px-3 py-2" data-testid={`revision-diff-${rev.id}-${change.field}`}>
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">{change.field}</span>
                                {change.oldValue && (
                                  <div className="flex gap-1.5 text-xs mb-0.5">
                                    <span className="text-red-400/70 shrink-0">−</span>
                                    <span className="text-muted-foreground/60 line-through line-clamp-1">{change.oldValue}</span>
                                  </div>
                                )}
                                {change.newValue && (
                                  <div className="flex gap-1.5 text-xs">
                                    <span className="text-green-400/70 shrink-0">+</span>
                                    <span className="text-foreground/70 line-clamp-2">{change.newValue}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {pageState === "chatting" && (
            <div className="relative bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="chat-section">
              {!activeWorkspaceId && (
                <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[1px] flex items-center justify-center rounded-2xl border border-border/50">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-xl max-w-sm text-center space-y-3">
                    <Lock size={24} className="mx-auto text-muted-foreground/50" />
                    <p className="text-sm font-medium text-foreground">Select a workspace to use Refine</p>
                    <p className="text-xs text-muted-foreground">You must have an active workspace selected to chat with Draper about your strategy.</p>
                  </div>
                </div>
              )}
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Refine with Draper</h3>
              <div ref={scrollRef} className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar">
                {chatMessages.length === 0 && (
                  <div className="py-8 text-center space-y-2">
                    {!analysis ? (
                      <p className="text-sm text-muted-foreground/50 italic px-4">Generate your marketing strategy first to unlock Draper refinements.</p>
                    ) : (
                      <p className="text-sm text-muted-foreground/50">Ask Draper to adjust the strategy, focus on specific areas, or explain the reasoning.</p>
                    )}
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className="space-y-2">
                    <div
                      className={`rounded-xl px-4 py-3 text-sm max-w-[80%] ${
                        msg.role === "user" ? "bg-blue-600/20 text-blue-200 ml-auto" : "bg-muted/50 text-foreground/80"
                      }`}
                      data-testid={`chat-message-${i}`}
                    >
                      {msg.content}
                    </div>
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="space-y-2 ml-2">
                        {msg.actions.map((action, j) => (
                          action.type === "link" ? (
                            <button
                              key={j}
                              onClick={() => navigate(action.href || "/strategy")}
                              className="flex items-center gap-2 px-3 py-2 bg-blue-600/15 border border-blue-500/30 rounded-lg text-xs font-semibold text-blue-400 hover:bg-blue-600/25 transition-colors"
                              data-testid={`chat-action-link-${i}-${j}`}
                            >
                              <ExternalLink size={12} /> {action.label}
                            </button>
                          ) : action.type === "ripple_offer" ? (
                            <div key={j} className="border border-amber-500/30 rounded-lg p-3 bg-amber-950/20 space-y-2" data-testid={`chat-action-ripple-${i}-${j}`}>
                              <p className="text-xs font-semibold text-amber-400">{action.label}</p>
                              {action.description && <p className="text-[11px] text-muted-foreground/60">{action.description}</p>}
                              <button
                                onClick={() => regenerateMutation.mutate()}
                                disabled={regenerateMutation.isPending}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600/20 border border-amber-500/30 rounded-lg text-xs font-bold text-amber-300 hover:bg-amber-600/30 disabled:opacity-50 transition-colors"
                                data-testid={`button-regenerate-content-${i}`}
                              >
                                {regenerateMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                                {regenerateMutation.isPending ? "Regenerating..." : "Yes, Regenerate"}
                              </button>
                            </div>
                          ) : null
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {refineMutation.isPending && (
                  <div className="flex items-center gap-2 text-muted-foreground/70 text-sm">
                    <Loader2 size={14} className="animate-spin" /> Draper is thinking...
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="input-chat"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendChat()}
                  placeholder="Ask Draper to refine the strategy..."
                  className="flex-1 bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 disabled:opacity-50"
                  disabled={!activeWorkspaceId || refineMutation.isPending || (!analysis && chatMessages.length === 0)}
                />
                <button
                  data-testid="button-send-chat"
                  onClick={handleSendChat}
                  disabled={!chatInput.trim() || refineMutation.isPending || !activeWorkspaceId || (!analysis && chatMessages.length === 0)}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-xl px-4 py-3 transition-colors flex items-center justify-center min-w-[44px]"
                >
                  {refineMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
